/**
 * Build a markdown section describing the platform context in which the
 * agent is running. Rendered into the system prompt alongside resource and environment sections.
 */

import type { AgentContextConfig } from "@skaile/workspaces/core";

const PLATFORM_DESCRIPTIONS: Record<string, string> = {
  skaile:
    "You are an AI assistant operating within the Skaile platform, an enterprise AI workspace. Users interact with you through a chat interface in their browser.",
};

/**
 * Build a markdown section describing the platform context in which the
 * agent is running. Rendered into the system prompt alongside resource
 * and environment sections.
 *
 * @param context - Platform context configuration (platform, multi_user, session_model)
 * @returns Markdown section string, or undefined when no context is configured
 * @docLink packages/runner/dev-guide#flow-execution-turn-based-model
 */
export function buildContextSection(context: AgentContextConfig | undefined): string | undefined {
  if (!context) return undefined;
  const hasAny =
    context.platform !== undefined ||
    context.multi_user !== undefined ||
    context.session_model !== undefined;
  if (!hasAny) return undefined;

  const lines: string[] = ["## Platform", ""];

  if (context.platform) {
    const description = PLATFORM_DESCRIPTIONS[context.platform];
    if (description) {
      lines.push(description);
    } else {
      lines.push(`You are running inside the ${context.platform} platform.`);
    }
    lines.push("");
  }

  if (context.multi_user === true) {
    lines.push(
      "Multiple users may participate in the same session. Be aware that messages in this conversation may come from different users.",
    );
    lines.push("");
  } else if (context.multi_user === false) {
    lines.push("This is a single-user session.");
    lines.push("");
  }

  if (context.session_model === "persistent") {
    lines.push(
      "Your work persists across interactions. Files you create or modify in the workspace are saved and available in future sessions.",
    );
    lines.push("");
  } else if (context.session_model === "ephemeral") {
    lines.push(
      "This session is ephemeral. Work you do does not persist beyond the current interaction.",
    );
    lines.push("");
  }

  // Trim trailing empty line
  while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();

  return lines.join("\n");
}
